import React, { useContext, useEffect, useRef, useState } from 'react';
import classes from '../PostEditorModal/PostEditorModal.module.scss';
import { PostEditorModal } from '../PostEditorModal';
import { SubmitErrorHandler, useForm, UseFormReturn } from 'react-hook-form';
import { PostEditorHeader } from '../PostEditorHeader';
import { PostEditorBody } from '../PostEditorBody';
import { SubmitHandler } from 'react-hook-form/dist/types/form';
import { PostEditorGroupDataProvider } from '../states/PostEditorGroupDataProvider';
import {
  OnSubmitErrorPostEditorFunction,
  OnSubmitPostEditorFunction
} from '../PostEditorModal/PostEditorModal.controller';
import { FormOptions } from '../../../constants/form';
import { usePostEditorNew } from '../../../hooks/usePostEditorNew';
import {
  PostEditorContextEnum,
  PostEditorSubmitData,
  PostEditorSubmitErrorPayload,
  PostEditorSubmitPayload
} from '../../../models/post-editor';
import { PostEditorProvider } from '../states/PostEditorProvider';
import { AppContext, isPostEditorContextTypeNewGuard } from '../../../providers/AppProvider';
import { PlanUsageType, UsageFieldsFragmentDoc, useCreatePostMutation } from '../../../models/graphql';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { showToast } from '../../../helpers/showToast';
import { isObject } from 'lodash';
import { useSettings } from '../../../hooks/useSettings';
import { setProfileSelectorFormValues } from '../../../components/socialProfiles/SocialProfileSelector/SocialProfileSelector.view';
import { mapAddAnotherFromCreate } from '../helpers/mapAddAnotherFromCreate';
import { useClosePostEditor } from '../../../hooks/useClosePostEditor';
import { useApolloClient } from '@apollo/client';
import { mapStateToPostInput } from '../helpers/mapStateToPostInput';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';

export const PostEditorNewView = (): JSX.Element => {
  const formInstance: UseFormReturn = useForm({
    ...FormOptions
  });
  const { setPostEditorContext, postEditorContext } = useContext(AppContext);
  if (!isPostEditorContextTypeNewGuard(postEditorContext))
    throw new Error('Post Editor New has been called with wrong context value: ' + JSON.stringify(postEditorContext));
  const { closePostEditor } = useClosePostEditor();
  const overrideWorkspaceSettings = !!postEditorContext.overrideWorkspaceSettings;
  const { loading, initialState, profiles, categories, error } = usePostEditorNew(
    postEditorContext.initialState,
    overrideWorkspaceSettings
  );
  const ref = useRef<HTMLDivElement | null>(null);

  // Set form state for the profiles after loading.
  useEffect(() => {
    if (!loading && profiles && initialState) {
      const activeProfiles = initialState.postGroups[0].profiles;
      setProfileSelectorFormValues(profiles, activeProfiles, formInstance, 'postGroup_0_profiles');
    }
  }, [formInstance, initialState, loading, profiles]);

  const createPostHookResult = useCreatePostMutation();
  const { selectedWorkspace } = useSelectedOrganization();
  const { selectedWorkspaceSettings } = useSettings();
  const onClosePostEditor = postEditorContext.onClose;
  const { setLimitReachedModalState } = useContext(AppContext);
  const { remainingPosts, getUsageFieldsLazyQuery } = useOrganizationPlan();
  const submitted = useRef<boolean>(false);

  const isErrorGuard = (value: unknown): value is Error => {
    return isObject(value) && 'name' in value && 'message' in value;
  };

  const { cache } = useApolloClient();

  const updateCache = (qtyPosts: number) => {
    const oldUsage: PlanUsageType | null = cache.readFragment({
      fragment: UsageFieldsFragmentDoc,
      fragmentName: 'UsageFields',
      id: 'PlanUsageType:{}'
    });
    const newUsage = { ...oldUsage, posts: (oldUsage?.posts ?? 0) + qtyPosts };
    cache.writeFragment({
      fragment: UsageFieldsFragmentDoc,
      fragmentName: 'UsageFields',
      id: 'PlanUsageType:{}',
      data: newUsage
    });
  };
  const onSubmitHandler: OnSubmitPostEditorFunction<SubmitHandler<PostEditorSubmitData>> =
    (dispatch, profiles, state) => async data => {
      try {
        if (!selectedWorkspace || profiles === undefined || !selectedWorkspaceSettings) {
          throw new Error('No Selected Workspace or selected profiles are undefined.');
        }
        const { multiMediaAttachMode } = selectedWorkspaceSettings;
        const payload: PostEditorSubmitPayload = {
          ref,
          submitted,
          profiles,
          workspaceId: selectedWorkspace.id,
          data,
          type: 'single',
          context: 'create',
          callback: success => {
            onClosePostEditor && onClosePostEditor(success);
            closePostEditor();
            // todo this is not a proper way of doing it. we should grab created posts and update cache in the effect
            const defaultQueuePosition = selectedWorkspaceSettings.defaultQueuePosition;
            if (success) {
              const { createPosts } = mapStateToPostInput(state, profiles, defaultQueuePosition);
              createPosts && updateCache(createPosts[0].posts.length);
            }
            if (data.addAnother?.includes('on')) {
              // we need to set timeout to 1ms in order to make sure that we do remove the post editor and bring it on again.
              setTimeout(() => {
                setPostEditorContext({
                  type: PostEditorContextEnum.new,
                  initialState: mapAddAnotherFromCreate(state, multiMediaAttachMode),
                  overrideWorkspaceSettings: true
                });
              }, 1);
            }
          },
          defaultQueuePosition: selectedWorkspaceSettings.defaultQueuePosition,
          onPlanLimitException: () => {
            setLimitReachedModalState({
              type: 'posts',
              isOpen: true,
              remaining: remainingPosts > 0 ? remainingPosts : undefined
            });
            submitted.current = false;
          },
          createPostHookResult
        };
        // We want to update the values of usage in case we hit a limit
        getUsageFieldsLazyQuery();
        dispatch({ type: 'submitEditor', payload });
      } catch (e) {
        const detail = isErrorGuard(e) ? e.message : 'Could not submit, error occurred of unknown type.';
        showToast({
          summary: 'Submit Error',
          detail: detail,
          severity: 'error'
        });
      }
    };

  const onErrorHandler: OnSubmitErrorPostEditorFunction<SubmitErrorHandler<PostEditorSubmitData>> = dispatch => () => {
    const payload: PostEditorSubmitErrorPayload = {
      type: 'single'
    };
    submitted.current = false;
    dispatch({ type: 'submitErrorEditor', payload });
  };

  useEffect(() => {
    if (error) {
      onClosePostEditor && onClosePostEditor(false);
      closePostEditor();
    }
  }, [error, closePostEditor, onClosePostEditor]);

  const [initialised, setInitialised] = useState<boolean>(false);

  return (
    <>
      {!initialised ? (
        <PostEditorModal
          title={'New Post'}
          formInstance={formInstance}
          loading={true}
          onErrorHandler={onErrorHandler}
          onSubmitHandler={onSubmitHandler}>
          <></>
        </PostEditorModal>
      ) : null}
      {initialState && !loading ? (
        <PostEditorProvider
          initialState={initialState}
          loading={loading}
          categories={categories}
          profiles={profiles}
          {...(postEditorContext.payload ? { payload: postEditorContext.payload } : {})}>
          <PostEditorModal
            title={'New Post'}
            formInstance={formInstance}
            loading={loading}
            setInitialised={setInitialised}
            onErrorHandler={onErrorHandler}
            onSubmitHandler={onSubmitHandler}>
            <PostEditorGroupDataProvider postGroupIndex={0}>
              <div ref={ref} className={classes.ContentWrapper}>
                <PostEditorHeader formInstance={formInstance} />
                <PostEditorBody formInstance={formInstance} />
              </div>
            </PostEditorGroupDataProvider>
          </PostEditorModal>
        </PostEditorProvider>
      ) : null}
    </>
  );
};
